import { mapGetters, mapActions, mapMutations } from 'vuex'

import parseData from '@/utils/parseData'
import ChartComponent from '@/components/Chart/Chart.vue'
import Dendrogram from '@/components/Dendrogram/Dendrogram.vue'
import ChatGPT from '@/components/ChatGPT/ChatGPT.vue'
import MarkdownEditor from '@/components/MarkdownEditor/MarkdownEditor.vue'
import { statuses, priorities, funnel, actions, types, intents } from '@/utils/clusterVariables.js'
import { buildCmsUploadDecision, isCmsUploadDecisionEnabled } from '@/utils/cmsUploadDecision'

export default {
  name: 'Article',
  data () {
    return {
      isLoading: {
        article: false,
        setPriority: false,
        setFunnel: false,
        setStatus: false,
        setShowInMap: false,
        score: false,
        targetUrl: false,
        selectableTargetUrls: false,
        updatePageAction: false,
        generating: false,
        autoFill: false,
        chartDataPotential: false,
        chartDataImpressions: false,
        deleteArticles: false,
        mergeCluster: false,
        unexcludeKeywords: false,
        generateBrief: false,
        generateDraft: false,
        rewrite: false,
        upload: false,
        refreshEditorContent: false
      },
      // Enhanced autosave configuration for each field
      autosave: {
        brief: {
          timeout: null,
          delay: 1000,
          retryCount: 0,
          maxRetries: 3,
          retryDelay: 2000,
          lastSavedContent: '',
          saveInProgress: false,
          hasError: false,
          pendingSave: false
        },
        research: {
          timeout: null,
          delay: 1000,
          retryCount: 0,
          maxRetries: 3,
          retryDelay: 2000,
          lastSavedContent: '',
          saveInProgress: false,
          hasError: false,
          pendingSave: false
        },
        draft: {
          timeout: null,
          delay: 1000,
          retryCount: 0,
          maxRetries: 3,
          retryDelay: 2000,
          lastSavedContent: '',
          saveInProgress: false,
          hasError: false,
          pendingSave: false
        },
        finalDraft: {
          timeout: null,
          delay: 1000,
          retryCount: 0,
          maxRetries: 3,
          retryDelay: 2000,
          lastSavedContent: '',
          saveInProgress: false,
          hasError: false,
          pendingSave: false
        }
      },
      outlineBuilder: false,
      searchKeywords: '',
      dialogDelete: false,
      dialogMerge: false,
      dialogDeleteCompeting: false,
      dialogModifyArticle: false,
      dialogKey: 0,
      cmMetricsOnly: false,

      title: null,
      description: '',
      research: '',
      draft: '',
      finalDraft: '',
      mergeWith: null,
      notesPreviewMode: false,
      notesPreviewObject: {
        brief: '',
        research: '',
        draft: '',
        final: ''
      },

      selectedCompetitors: [],
      selectableTargetUrls: [],
      relatedArticlesById: {},
      competingClusters: [],

      statuses,
      priorities,
      funnel,
      actions,
      types,
      intents,

      tableArticleKeywords: {
        isLoading: false,
        headers: [
          { text: 'Keywords', value: 'keyword' },
          { text: 'Cluster Rank', value: 'google_position_last' },
          { text: 'Cluster Clicks', value: 'google_clicks_last' },
          { text: 'Cluster Impressions', value: 'google_impressions_last' },
          { text: 'Search Volume', value: 'estimated_search_volume' }
        ],
        sortBy: 'google_impressions_last',
        descending: true
      },

      tablePageKeywords: {
        isLoading: false,
        headers: [
          { text: 'Cluster', value: 'title' },
          { text: 'Keywords', value: 'keyword' },
          { text: 'SERP Overlap', value: 'score' },
          { text: 'Impressions', value: 'target_impressions' },
          { text: 'Actions', value: 'id' }
        ],
        sortBy: 'target_impressions',
        descending: true
      },

      tableClusterPages: {
        isLoading: false,
        headers: [
          { text: 'URL', value: 'url' },
          { text: 'Matching Cluster', value: 'title' },
          { text: 'Impressions', value: 'target_impressions' }
        ],
        sortBy: 'target_impressions',
        descending: true
      },

      tableCompetitors: {
        isLoading: false,
        headers: [
          { text: 'Top 5 Competitors', value: 'url' },
          { text: '# of Keywords', value: 'keywords' },
          { text: 'Rank', value: 'rank' }
        ]
      },

      tableHoverKeywords: {
        isLoading: false,
        headers: [
          { text: 'Keyword', value: 'keyword' },
          { text: 'Impressions', value: 'target_impressions' }
        ],
        sortBy: 'target_impressions',
        descending: true
      },

      tableInternalLinks: {
        isLoading: false,
        headers: [
          { text: 'URL', value: 'url' },
          { text: 'Anchor text', value: 'anchor_text' }
        ],
        sortBy: 'url',
        descending: true
      },

      articlesNewPriority: null,
      articlesNewFunnel: null,
      articlesNewStatus: null,
      articlesNewPageId: null,
      articlesNewCustomUrl: null,
      showChangeTargetUrl: false,
      notesTabs: [
        { text: 'Brief', value: 'brief' },
        { text: 'Research', value: 'research' },
        { text: 'Draft', value: 'draft' },
        { text: 'Final Draft', value: 'finalDraft' }
      ],
      notesTab: 0,

      newUrlRules: value => /^(http|https):\/\/[^ "]+$/.test(value) || 'Invalid URL.',

      chartDataImpressions: null,
      chartDataPotential: null,
      chartOptionsPotential: {
        scales: {
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Rank'
            },
            ticks: {
              min: 1,
              max: 100,
              reverse: true,
              padding: 10
            },
            gridLines: {
              drawOnChartArea: false,
              drawTicks: false
            }
          }],
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Impressions'
            },
            gridLines: {
              drawOnChartArea: false,
              drawTicks: false
            },
            ticks: {
              beginAtZero: true,
              padding: 10,
              suggestedMax: 100
            }
          }]
        },
        legend: {
          display: false
        },
        animation: {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        responsive: true,
        aspectRatio: 1.2,
        tooltips: {
          callbacks: {
            title: function (tooltipItem, data) {
              return data.labels[tooltipItem[0].index]
            },
            label: function (tooltipItem, data) {
              return data.datasets[0].label[1] + ': ' + data.datasets[0].data[tooltipItem.index].y + ', ' + data.datasets[0].label[0] + ': ' + data.datasets[0].data[tooltipItem.index].x
            }
          }
        }
      },
      chartOptionsImpressions: {
        scales: {
          yAxes: [{
            type: 'linear',
            position: 'left',
            display: false,
            gridLines: {
              drawOnChartArea: false,
              drawTicks: false
            },
            id: 'y-axis-1',
            min: 0,
            ticks: {
              beginAtZero: true,
              suggestedMax: 10
            },
            stacked: false
          }],
          xAxes: [{
            type: 'time',
            display: false,
            gridLines: {
              drawOnChartArea: false,
              drawTicks: false
            },
            time: {
              unit: 'week',
              isoWeekday: true,
              displayFormats: {
                day: 'MMM DD'
              }
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          position: 'nearest'
        },
        legend: {
          display: false
        },
        animation: {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        aspectRatio: 6
      },
      date: null,
      saveDelay: 1000,
      showRewriteDialog: false,
      rewriteStatus: null,
      statusOptions: [
        { text: 'No status - rewrite all steps', value: 'no status' },
        { text: 'Briefed - rewrite research, draft and final draft, add new images', value: 'briefed' },
        { text: 'Researched - rewrite draft and final draft, add new images', value: 'researched' },
        { text: 'Drafted - rewrite only final draft, add new images', value: 'drafted' },
        { text: 'Edited - add new images', value: 'edited' }
      ],
      showCopilot: false,
      uploadDialog: {
        open: false,
        title: 'Confirm upload',
        message: '',
        detail: '',
        confirmLabel: 'Confirm',
        confirmColor: 'accent',
        cancelLabel: 'Cancel'
      },
      uploadDialogResolver: null
    }
  },
  async mounted () {
    await this.loadData()
  },
  beforeDestroy () {
    // Clean up all autosave timeouts on component destruction
    this.clearAutosave('brief')
    this.clearAutosave('research')
    this.clearAutosave('draft')
    this.clearAutosave('finalDraft')
    if (this.uploadDialogResolver) this.uploadDialogResolver(false)
  },
  watch: {
    async $route () {
      await this.loadData()
    },
    description: {
      handler (newValue) {
        if (newValue !== undefined && newValue !== this.autosave.brief.lastSavedContent) {
          this.scheduleAutosave('brief')
        }
      },
      immediate: false
    },
    research: {
      handler (newValue) {
        if (newValue !== undefined && newValue !== this.autosave.research.lastSavedContent) {
          this.scheduleAutosave('research')
        }
      },
      immediate: false
    },
    draft: {
      handler (newValue) {
        if (newValue !== undefined && newValue !== this.autosave.draft.lastSavedContent) {
          this.scheduleAutosave('draft')
        }
      },
      immediate: false
    },
    finalDraft: {
      handler (newValue) {
        if (newValue !== undefined && newValue !== this.autosave.finalDraft.lastSavedContent) {
          this.scheduleAutosave('finalDraft')
        }
      },
      immediate: false
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'article',
      'keywords',
      'articleUrls',
      'articleExternalUrls',
      'score',
      'pages',
      'pageKeywords',
      'gsc',
      'internalLinks',
      'topics',
      'activeIntegration',
      'activeContentplan',
      'activeWorkspace'
    ]),
    safeArticleKeywords () {
      if (Array.isArray(this.keywords)) {
        return this.keywords
      } else if (this.keywords && Array.isArray(this.keywords.items)) {
        return this.keywords.items
      }
      return []
    },
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    articleId () {
      return parseInt(this.$route.params.articleId)
    },
    activeTopic () {
      if (!this.article || !this.topics) return 'No hub'
      return this.topics.find(t => t.id === this.article?.topic_id)?.name || 'No hub'
    },
    pageId () {
      return this.article?.page_id
    },
    sharingKey () {
      return this.$route.params.sharingKey
    },
    competingPages () {
      if (!this.article || !this.articleUrls) return

      return this.articleUrls.filter(url => url.url !== this.article.url && url.page_impressions_last > 0) || null
    },
    mergeWithTarget () {
      if (!this.article || !this.pages) return
      const pages = this.pages?.items || []
      const mergeObject = pages.find(p => p.page_id === this.article.merge_with)

      return {
        url: mergeObject?.url,
        clusterId: mergeObject?.article_id,
        clusterTitle: mergeObject?.title
      }
    },
    urlMatchMetrics () {
      if (!this.article || !this.articleUrls) return
      const urlMatch = this.articleUrls.filter(page => page.url === this.article.url)
      return urlMatch?.length ? urlMatch : null
    },
    sumOfImpressions () {
      if (!this.article) return 0
      const urlImpressions = this.urlMatchMetrics?.[0].page_impressions_last || 0
      const clusterImpressions = this.article.google_impressions_last || 0
      const clusterMatchImpressions = this.article.target_impressions || 0

      return clusterMatchImpressions + (clusterImpressions - clusterMatchImpressions) + (urlImpressions - clusterMatchImpressions)
    },
    getWidthUrl () {
      if (!this.article || !this.urlMatchMetrics) return { width: '0' }
      return { width: (this.urlMatchMetrics[0].page_impressions_last - this.article.target_impressions) / this.urlMatchMetrics[0].page_impressions_last * 100 + '%' }
    },
    getWidthTargetUrl () {
      if (!this.article || !this.urlMatchMetrics) return { width: '0' }
      return { width: this.article.target_impressions / this.urlMatchMetrics[0].page_impressions_last * 100 + '%' }
    },
    getWidthCluster () {
      if (!this.article || !this.urlMatchMetrics) return { width: '0' }
      return { width: (this.article.google_impressions_last - this.article.target_impressions) / this.article.google_impressions_last * 100 + '%' }
    },
    getWidthTargetCluster () {
      if (!this.article || !this.urlMatchMetrics) return { width: '0' }
      return { width: this.article.target_impressions / this.article.google_impressions_last * 100 + '%' }
    },
    getWidthTarget () {
      if (!this.article || !this.urlMatchMetrics) return { width: '0' }
      return { width: this.article.target_impressions / this.sumOfImpressions * 100 + '%' }
    },
    publishDateFormatted () {
      if (!this.article || !this.article.published_at) return ''
      return new Date(this.article.published_at).toISOString().slice(0, 10)
    },
    competingPagesWithoutMatch () {
      if (!this.competingPages) return
      return this.competingPages.filter(page => this.getArticleTitle(page.article_id) === '-')
    },
    competingPagesWithMatch () {
      if (!this.competingPages) return
      return this.competingPages.filter(page => this.getArticleTitle(page.article_id) !== '-')
    },
    competingClustersWithoutMatch () {
      if (!this.competingClusters) return
      return this.competingClusters.filter(cluster => !this.hasPageForArticle(cluster.article_id) && cluster.article_id !== 'excluded')
    },
    competingClustersWithMatch () {
      if (!this.competingClusters) return
      return this.competingClusters.filter(cluster => cluster.article_id === 'excluded' || this.hasPageForArticle(cluster.article_id))
    },
    isRewriteStatusValid () {
      if (!this.article) return false // Cannot determine validity without the article
      const allowedStatuses = ['no status', 'briefed', 'researched', 'drafted', 'edited']
      const statusToCheck = this.rewriteStatus || this.article.status // Use selected status or current article status
      return allowedStatuses.includes(statusToCheck)
    },
    activeIntegrationDomain () {
      return this.activeIntegration?.wp_domain
    },
    isArticleEmptyOfContent () {
      if (!this.article) return true
      return !this.article.description && !this.article.research && !this.article.draft && !this.article.final
    },
    isUploadDisabled () {
      return this.activeContentplan?.main_cms_integration === 'none'
    },
    uploadDisabledMessage () {
      return 'Please set up a CMS integration in Contentplan Settings before uploading articles'
    },
    // Enhanced computed properties for autosave states
    isSavingBrief () {
      return this.autosave.brief.saveInProgress
    },
    isSavingResearch () {
      return this.autosave.research.saveInProgress
    },
    isSavingDraft () {
      return this.autosave.draft.saveInProgress
    },
    isSavingFinalDraft () {
      return this.autosave.finalDraft.saveInProgress
    },
    hasBriefSaveError () {
      return this.autosave.brief.hasError
    },
    hasResearchSaveError () {
      return this.autosave.research.hasError
    },
    hasDraftSaveError () {
      return this.autosave.draft.hasError
    },
    hasFinalDraftSaveError () {
      return this.autosave.finalDraft.hasError
    }
  },
  methods: {
    parseData,
    getUploadDialogColor (severity) {
      if (severity === 'error') return 'error'
      if (severity === 'warning') return 'warning'
      return 'accent'
    },
    openUploadDialog ({ title, message, detail, confirmLabel, severity }) {
      if (this.uploadDialogResolver) this.uploadDialogResolver(false)
      this.uploadDialog = {
        open: true,
        title: title || 'Confirm upload',
        message: message || '',
        detail: detail || '',
        confirmLabel: confirmLabel || 'Confirm',
        confirmColor: this.getUploadDialogColor(severity),
        cancelLabel: 'Cancel'
      }

      return new Promise(resolve => {
        this.uploadDialogResolver = resolve
      })
    },
    closeUploadDialog (confirmed) {
      this.uploadDialog.open = false
      if (this.uploadDialogResolver) this.uploadDialogResolver(confirmed)
      this.uploadDialogResolver = null
    },
    cancelUploadDialog () {
      this.closeUploadDialog(false)
    },
    confirmUploadDialog () {
      this.closeUploadDialog(true)
    },
    async getUploadArticleSnapshot (articleId) {
      const response = await this.articleGet({
        contentplanId: this.contentplanId,
        filters: [{ field: 'articleId', op: 'eq', value: articleId }],
        includeExtendedMetrics: false,
        straightReturn: true,
        skipCommit: true
      })
      return response?.items?.[0] || null
    },
    async getUploadPageSnapshot (pageId) {
      if (!pageId) return null
      const response = await this.pageGet({
        contentplanId: this.contentplanId,
        pageId,
        skipCommit: true
      })
      return response?.items?.[0] || null
    },
    async prepareUploadDecisionForArticle (articleId) {
      const integration = this.activeContentplan?.main_cms_integration
      const article = await this.getUploadArticleSnapshot(articleId)
      if (!article) throw new Error('Article not found')

      if (!isCmsUploadDecisionEnabled(integration)) {
        const decision = buildCmsUploadDecision({ integration, article, page: null })
        return { article, decision }
      }

      const page = await this.getUploadPageSnapshot(article.page_id)
      const decision = buildCmsUploadDecision({ integration, article, page })
      return { article, decision }
    },
    async confirmSingleUpload (articleTitle, decision) {
      return this.openUploadDialog({
        title: articleTitle ? `Upload "${articleTitle}"` : 'Confirm upload',
        message: decision.message,
        detail: decision.detail,
        confirmLabel: decision.confirmLabel,
        severity: decision.severity
      })
    },
    // Enhanced autosave methods
    scheduleAutosave (field) {
      const autosaveConfig = this.autosave[field]
      if (autosaveConfig.saveInProgress) {
        autosaveConfig.pendingSave = true
        return
      }

      this.clearAutosaveTimeout(field)
      autosaveConfig.timeout = setTimeout(() => {
        this.performAutosave(field)
      }, autosaveConfig.delay)
    },
    clearAutosave (field) {
      const autosaveConfig = this.autosave[field]
      this.clearAutosaveTimeout(field)
      autosaveConfig.saveInProgress = false
      autosaveConfig.pendingSave = false
      autosaveConfig.hasError = false
      autosaveConfig.retryCount = 0
    },
    clearAutosaveTimeout (field) {
      const autosaveConfig = this.autosave[field]
      if (autosaveConfig.timeout) {
        clearTimeout(autosaveConfig.timeout)
        autosaveConfig.timeout = null
      }
    },
    async performAutosave (field) {
      const autosaveConfig = this.autosave[field]

      if (autosaveConfig.saveInProgress) return

      let contentToSave, action
      switch (field) {
        case 'brief':
          contentToSave = this.description
          action = 'updateDescription'
          break
        case 'research':
          contentToSave = this.research
          action = 'updateResearch'
          break
        case 'draft':
          contentToSave = this.draft
          action = 'updateDraft'
          break
        case 'finalDraft':
          contentToSave = this.finalDraft
          action = 'updateFinalDraft'
          break
        default:
          return
      }

      if (contentToSave === autosaveConfig.lastSavedContent) return

      autosaveConfig.saveInProgress = true
      autosaveConfig.hasError = false

      try {
        const payload = {
          contentplanId: this.contentplanId,
          action,
          articleIds: [this.articleId]
        }
        payload[field === 'brief' ? 'description' : field === 'finalDraft' ? 'final' : field] = contentToSave || null

        await this.articlePut(payload)

        // Update local state on successful save
        autosaveConfig.lastSavedContent = contentToSave
        autosaveConfig.retryCount = 0

        // Update preview object
        this.notesPreviewObject[field === 'brief' ? 'brief' : field === 'finalDraft' ? 'final' : field] = contentToSave

        console.log(`${field} saved successfully`)
      } catch (error) {
        console.error(`Failed to save ${field}:`, error)
        autosaveConfig.hasError = true

        // Retry logic
        if (autosaveConfig.retryCount < autosaveConfig.maxRetries) {
          autosaveConfig.retryCount++
          console.log(`Retrying ${field} save (attempt ${autosaveConfig.retryCount}/${autosaveConfig.maxRetries})`)

          setTimeout(() => {
            this.performAutosave(field)
          }, autosaveConfig.retryDelay * autosaveConfig.retryCount)
        } else {
          this.setSnackbar({
            open: true,
            text: `Failed to save ${field} after multiple attempts. Please try again later.`,
            type: 'error'
          })
        }
      } finally {
        autosaveConfig.saveInProgress = false

        // Handle pending save if there was one
        if (autosaveConfig.pendingSave) {
          autosaveConfig.pendingSave = false
          this.scheduleAutosave(field)
        }
      }
    },

    ...mapActions([
      'articleGet',
      'topicGetList',
      'keywordGet',
      'loadArticleUrls',
      'loadArticleExternalUrls',
      'articlePut',
      'loadScoreAndCommit',
      'loadScoreAndReturn',
      'pagePost',
      'pageGet',
      'pagePut',
      'keywordGetListByPage',
      'keywordPut',
      'getGSCReportBundle',
      'recalculateAlerts',
      'internalLinksGetListByContentplan',
      'getIntegrations',
      'sendWebsocketMessage',
      'uploadArticle'
    ]),
    ...mapMutations([
      'addProgressTracker',
      'setSnackbar',
      'addArticleUrlIfMissing'
    ]),
    getStepsObjectFromStatus (status) {
      let stepsObject = {}
      switch (status) {
        case 'briefed':
          stepsObject = { Briefing: 'done', Researching: 'inProgress', Drafting: 'pending', Editing: 'pending', Illustrating: 'pending' }
          break
        case 'researched':
          stepsObject = { Briefing: 'done', Researching: 'done', Drafting: 'inProgress', Editing: 'pending', Illustrating: 'pending' }
          break
        case 'drafted':
          stepsObject = { Briefing: 'done', Researching: 'done', Drafting: 'done', Editing: 'inProgress', Illustrating: 'pending' }
          break
        case 'edited':
          stepsObject = { Briefing: 'done', Researching: 'done', Drafting: 'done', Editing: 'done', Illustrating: 'inProgress' }
          break
        case 'no status':
        default:
          stepsObject = { Briefing: 'inProgress', Researching: 'pending', Drafting: 'pending', Editing: 'pending', Illustrating: 'pending' }
          break
      }
      return stepsObject
    },
    async loadData () {
      this.isLoading.article = true
      this.isLoading.chartDataPotential = true
      this.selectableTargetUrls = []
      this.relatedArticlesById = {}
      try {
        await Promise.all([
          this.keywordGet({
            contentplanId: this.contentplanId,
            articleId: this.articleId,
            sharingKey: this.sharingKey
          }),
          this.loadArticleUrls({
            articleId: this.articleId,
            sharingKey: this.sharingKey
          }),
          this.loadArticleExternalUrls({
            articleId: this.articleId,
            sharingKey: this.sharingKey
          }),
          this.articleGet({
            contentplanId: this.contentplanId,
            articleId: this.articleId,
            sharingKey: this.sharingKey,
            includeExtendedMetrics: true
          }),
          this.internalLinksGetListByContentplan({
            contentplanId: this.contentplanId,
            articleId: this.articleId,
            sharingKey: this.sharingKey
          }),
          this.getIntegrations({ contentplanId: this.contentplanId })
        ])
      } catch (e) {
        console.error(e)
      }
      this.isLoading.article = false

      this.description = this.article?.description || ''
      this.research = this.article?.research || ''
      this.draft = this.article?.draft || ''
      this.finalDraft = this.article?.final || ''
      this.title = this.article?.title || ''
      document.title = this.article?.title + ' | ContentGecko'
      this.notesPreviewObject = {
        brief: this.article?.description || '',
        research: this.article?.research || '',
        draft: this.article?.draft || '',
        final: this.article?.final || ''
      }

      // Initialize autosave states
      this.autosave.brief.lastSavedContent = this.description
      this.autosave.research.lastSavedContent = this.research
      this.autosave.draft.lastSavedContent = this.draft
      this.autosave.finalDraft.lastSavedContent = this.finalDraft
      this.getChartDataPotential()
      this.getChartDataCMR()

      if (this.user) {
        await this.topicGetList({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        })
      }

      if (this.article.page_id) {
        this.addArticleUrlIfMissing({
          url: this.article.url,
          article_id: this.article.id,
          article_id_locked: this.article.article_id_locked,
          page_impressions_last: this.article.page_impressions_last ?? 0,
          page_impressions_prev: this.article.page_impressions_prev ?? 0,
          page_clicks_last: this.article.page_clicks_last ?? 0,
          page_clicks_prev: this.article.page_clicks_prev ?? 0,
          page_position_last: this.article.page_position_last ?? 0,
          page_position_prev: this.article.page_position_prev ?? 0,
          page_ctr_last: this.article.page_ctr_last ?? 0,
          page_ctr_prev: this.article.page_ctr_prev ?? 0,
          id: 'N/a'
        })

        await this.keywordGetListByPage({
          contentplanId: this.contentplanId,
          pageId: this.article.page_id,
          sharingKey: this.sharingKey
        })
        this.groupKeywordsByCluster()
      }

      try {
        await this.loadRelatedArticles()
      } catch (e) {
        console.error(e)
      }
      this.getScoreAndCommit()
    },
    async handlePostWordpress () {
    },
    async getScoreAndCommit () {
      this.isLoading.score = true

      await this.loadScoreAndCommit({
        contentplanId: this.contentplanId,
        articleId: this.articleId
      })

      this.isLoading.score = false
    },
    async getScoreAndReturn (comparisonArticleId) {
      if (!comparisonArticleId) return
      if (typeof comparisonArticleId !== 'number') return

      const score = await this.loadScoreAndReturn({
        contentplanId: this.contentplanId,
        articleId: [this.articleId, comparisonArticleId]
      })
      return score
    },
    // Legacy methods maintained for compatibility but using new autosave system
    async saveArticleBrief () {
      await this.performAutosave('brief')
    },
    async saveArticleResearch () {
      await this.performAutosave('research')
    },
    async saveArticleDraft () {
      await this.performAutosave('draft')
    },
    async saveArticleFinalDraft () {
      await this.performAutosave('finalDraft')
    },
    async renameSelectedArticle () {
      this.articlePut({
        workspaceId: this.workspaceId,
        contentplanId: this.contentplanId,
        action: 'rename',
        articleIds: this.articleId,
        name: this.title || null
      })
    },
    async setPriority (value) {
      if (typeof value === 'undefined') return

      this.isLoading.setPriority = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updatePriority',
          articleIds: this.articleId,
          priority: value
        })
        await this.articleGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey,
          includeExtendedMetrics: true
        })
      } finally {
        this.isLoading.setPriority = false
      }
    },
    async setFunnel (value) {
      if (!value) return

      this.isLoading.setFunnel = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateFunnel',
          articleIds: this.articleId,
          funnel: value || this.articlesNewFunnel
        })
        await this.articleGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey,
          includeExtendedMetrics: true
        })
        this.articlesNewFunnel = null
      } finally {
        this.isLoading.setFunnel = false
      }
    },
    async setStatus (value) {
      if (!value) return

      this.isLoading.setStatus = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateStatus',
          articleIds: this.articleId,
          status: value || this.articlesNewStatus
        })
        await this.articleGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey,
          includeExtendedMetrics: true
        })
        this.articlesNewStatus = null
      } finally {
        this.isLoading.setStatus = false
      }
    },
    async setType (value) {
      if (!value) return

      this.isLoading.setType = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateType',
          articleIds: this.articleId,
          type: value
        })
        await this.articleGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey
        })
      } finally {
        this.isLoading.setType = false
      }
    },
    async setIntent (value) {
      if (!value) return

      this.isLoading.setIntent = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateIntent',
          articleIds: this.articleId,
          intent: value
        })
        await this.articleGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey,
          includeExtendedMetrics: true
        })
      } finally {
        this.isLoading.setIntent = false
      }
    },
    async setShowInMap (value) {
      if (typeof value === 'undefined') return

      this.isLoading.setShowInMap = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: value ? 'addToMap' : 'removeFromMap',
          articleIds: this.articleId
        })
        await this.articleGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey
        })
      } finally {
        this.isLoading.setShowInMap = false
      }
    },
    async updateUrlMatch () {
      if (!this.articlesNewPageId && !this.articlesNewCustomUrl) return

      this.isLoading.targetUrl = true
      let pageIdsToUpdate = [this.articlesNewPageId]

      try {
        // If custom URL is provided, first add the page
        if (this.articlesNewCustomUrl) {
          const newPagesIds = await this.pagePost({
            contentplanId: this.contentplanId,
            urls: [this.articlesNewCustomUrl]
          })
          if (!newPagesIds?.length) throw new Error('Could not create the new page.')
          pageIdsToUpdate = newPagesIds
        }

        // Now update the page association for the article
        await this.pagePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          pageIds: pageIdsToUpdate,
          action: 'setArticle'
        })
        await this.loadData()
      } catch (e) {
        console.error(e)
      } finally {
        this.articlesNewPageId = null
        this.articlesNewCustomUrl = null
        this.isLoading.targetUrl = false
        this.showChangeTargetUrl = false
      }
    },
    async handlePageDetailsMenu (isOpen) {
      if (!isOpen || this.isLoading.selectableTargetUrls || this.selectableTargetUrls.length) return
      await this.getInternalUrls()
    },
    getRelatedArticleIds () {
      const relatedArticleIdsFromUrls = Array.isArray(this.articleUrls)
        ? this.articleUrls.map(url => url.article_id)
        : []
      const relatedArticleIdsFromKeywords = this.article?.page_id && Array.isArray(this.pageKeywords)
        ? this.pageKeywords.map(keyword => keyword.article_id)
        : []

      return [...new Set([
        ...relatedArticleIdsFromUrls,
        ...relatedArticleIdsFromKeywords
      ].filter(articleId => articleId && articleId !== this.articleId))]
    },
    async loadRelatedArticles () {
      const articleIds = this.getRelatedArticleIds()
      if (!articleIds.length) return

      const response = await this.articleGet({
        contentplanId: this.contentplanId,
        filters: [{ field: 'articleId', op: 'in', value: articleIds }],
        pagination: { limit: articleIds.length, offset: 0 },
        straightReturn: true,
        skipCommit: true
      })

      this.relatedArticlesById = (response?.items || []).reduce((result, relatedArticle) => {
        result[relatedArticle.article_id] = relatedArticle
        return result
      }, {})
    },
    getRelatedArticle (articleId) {
      if (!articleId) return null
      if (articleId === this.articleId) return this.article
      return this.relatedArticlesById[articleId] || null
    },
    hasPageForArticle (articleId) {
      return Boolean(this.getRelatedArticle(articleId)?.page_id)
    },
    async getInternalUrls () {
      this.isLoading.selectableTargetUrls = true

      try {
        const response = await this.pageGet({
          contentplanId: this.contentplanId,
          excluded: '0',
          limit: 5000,
          skipCommit: true,
          sharingKey: this.sharingKey
        })
        const articleUrls = Array.isArray(this.articleUrls) ? this.articleUrls : []
        const pages = response?.items || []
        const urlsByPageId = [...articleUrls, ...pages].reduce((result, page) => {
          if (!page?.page_id) return result
          result[page.page_id] = {
            ...result[page.page_id],
            ...page,
            target_impressions: page.target_impressions ?? page.page_impressions_last ?? page.google_impressions_last ?? 0,
            page_impressions_last: page.page_impressions_last ?? page.google_impressions_last ?? 0,
            page_impressions_prev: page.page_impressions_prev ?? page.google_impressions_prev ?? 0,
            page_clicks_last: page.page_clicks_last ?? page.google_clicks_last ?? 0,
            page_clicks_prev: page.page_clicks_prev ?? page.google_clicks_prev ?? 0,
            page_position_last: page.page_position_last ?? page.google_position_last ?? 0,
            page_position_prev: page.page_position_prev ?? page.google_position_prev ?? 0
          }
          return result
        }, {})
        this.selectableTargetUrls = Object.values(urlsByPageId)
          .filter(url => url.article_id !== this.articleId)
          .map(url => ({
            ...url,
            title: url.title || this.getRelatedArticle(url.article_id)?.title || null
          }))
          .sort((a, b) => {
            if (a.target_impressions > b.target_impressions) return -1
            if (a.target_impressions < b.target_impressions) return 1
            if (a.url < b.url) return -1
            if (a.url > b.url) return 1
            return 0
          })
      } finally {
        this.isLoading.selectableTargetUrls = false
      }
    },
    async share (action) {
      await this.articlePut({
        contentplanId: this.contentplanId,
        action: 'share',
        articleIds: [this.articleId],
        share: action
      })

      await this.articleGet({
        contentplanId: this.contentplanId,
        articleId: this.articleId,
        includeExtendedMetrics: true
      })
    },
    suggestedActionText (action) {
      return this.actions.find(a => a.value === action)?.text
    },
    getArticleTitle (articleId) {
      return this.getRelatedArticle(articleId)?.title || '-'
    },
    async groupKeywordsByCluster () {
      // this.pageKeywords has rows of keywords and their impressions, but we need to group them by cluster
      const uniqueClusters = new Set(this.pageKeywords.filter(a => a.article_id && (a.article_id !== this.articleId))?.map(a => a.title))
      this.competingClusters = []

      uniqueClusters.forEach(cluster => {
        const clusterKeywords = this.pageKeywords.filter(a => a.title === cluster)
        const clusterImpressions = clusterKeywords.reduce((a, b) => a + b.target_impressions, 0)
        this.competingClusters.push({
          title: cluster,
          target_impressions: clusterImpressions,
          article_id: clusterKeywords[0].article_id
        })
      })

      // add keywords not in content plan to a separate row
      const keywordsNotInContentPlan = this.pageKeywords.filter(keyword => !keyword.article_id && !keyword.excluded && !keyword.in_queue)
      if (keywordsNotInContentPlan.length > 0) {
        const clusterImpressions = keywordsNotInContentPlan.reduce((a, b) => a + b.target_impressions, 0)
        this.competingClusters.push({
          title: 'Inactive keywords',
          target_impressions: clusterImpressions,
          article_id: 'not-in-plan'
        })
      }

      // add keywords in queue to a separate row
      const keywordsInQueue = this.pageKeywords.filter(keyword => keyword.in_queue)
      if (keywordsInQueue.length > 0) {
        const clusterImpressions = keywordsInQueue.reduce((a, b) => a + b.target_impressions, 0)
        this.competingClusters.push({
          title: 'Keywords in queue',
          target_impressions: clusterImpressions,
          article_id: 'in-queue'
        })
      }

      // add excluded keywords to a separate row
      const excludedKeywords = this.pageKeywords.filter(keyword => keyword.excluded)
      if (excludedKeywords.length > 0) {
        const clusterImpressions = excludedKeywords.reduce((a, b) => a + b.target_impressions, 0)
        this.competingClusters.push({
          title: 'Excluded keywords',
          target_impressions: clusterImpressions,
          article_id: 'excluded'
        })
      }

      this.competingClusters = this.competingClusters.sort((a, b) => b.target_impressions - a.target_impressions)

      this.competingClusters = await Promise.all(this.competingClusters.map(async cluster => {
        const score = await this.getScoreAndReturn(cluster.article_id, null)
        return {
          ...cluster,
          score
        }
      }))
    },
    clusterKeywords (articleId) {
      if (!this.pageKeywords) return

      if (articleId === 'not-in-plan') {
        return this.pageKeywords.filter(keyword => !keyword.article_id && !keyword.excluded && !keyword.in_queue)
      }

      if (articleId === 'in-queue') {
        return this.pageKeywords.filter(keyword => keyword.in_queue)
      }

      if (articleId === 'excluded') {
        return this.pageKeywords.filter(keyword => keyword.excluded)
      }

      return this.pageKeywords.filter(keyword => keyword.article_id === articleId)
    },
    async updatePageAction (action) {
      this.isLoading.updatePageAction = true

      try {
        await this.pagePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          pageIds: this.article.page_id,
          pageAction: action,
          action: 'setPageAction'
        })
        await Promise.all([
          this.pageGet({
            contentplanId: this.contentplanId,
            articleId: this.articleId
          }),
          this.articleGet({
            contentplanId: this.contentplanId,
            articleId: this.articleId,
            includeExtendedMetrics: true
          })
        ])
      } finally {
        this.isLoading.updatePageAction = false
      }
    },
    async getChartDataPotential () {
      if (!this.safeArticleKeywords?.length) return
      this.isLoading.chartDataPotential = true

      const dataset = {
        labels: this.safeArticleKeywords.map(keyword => keyword.keyword),
        datasets: [{
          label: ['Rank', 'Impressions'],
          data: this.safeArticleKeywords.map(keyword => ({
            x: Math.min(parseData(keyword.google_position_last) || 0, 100),
            y: keyword.google_impressions_last || 0
          })),
          backgroundColor: '#861086'
        }]
      }

      this.chartDataPotential = dataset
      this.isLoading.chartDataPotential = false
    },
    async getChartDataCMR () {
      this.isLoading.chartDataImpressions = true
      // date is 12 weeks up to now
      this.setDates(12)

      let gscDataArray = [{
        label: 'Cluster Match Impressions',
        backgroundColor: '#b6f1c6',
        borderColor: 'green',
        fill: true,
        data: null,
        yAxisID: 'y-axis-1'
      },
      {
        label: 'Cluster Impressions',
        backgroundColor: '#eae4f7',
        borderColor: '#5e35b1',
        fill: true,
        data: null,
        yAxisID: 'y-axis-1'
      },
      {
        label: 'URL Impressions',
        backgroundColor: '#dde9fd',
        borderColor: '#4285f4',
        fill: true,
        data: null,
        yAxisID: 'y-axis-1'
      }]

      try {
        gscDataArray = await Promise.all(gscDataArray.map(async obj => {
          const response = await this.getGSCReportBundle({
            contentplanId: this.contentplanId,
            startDate: this.date[0],
            endDate: this.date[1],
            articleIds: obj.label !== 'URL Impressions' ? [this.articleId] : null,
            pages: obj.label !== 'Cluster Impressions' ? [this.article.url] : null,
            sections: ['DATE'],
            commitToStore: false
          })
          obj.data = response?.byDate || []
          return obj
        }))

        if (gscDataArray.some(obj => typeof obj === 'string')) {
          console.log('Error', gscDataArray)
          this.isLoading.chartDataImpressions = false
          return
        }

        gscDataArray.forEach(obj => {
          const dateArray = obj?.data.map(x => x.keys[0])
          obj.data = obj?.data.map(x => x.impressions)
          this.calcChartData(dateArray, obj, 'chartDataImpressions')
        })
      } catch (error) {
        console.log(error)
      }

      this.isLoading.chartDataImpressions = false
    },
    async calcChartData (labels, dataset, target) {
      if (!this[target]) {
        this[target] = {
          labels,
          datasets: [dataset]
        }
      } else {
        this[target].datasets.push(dataset)
      }

      this[target].datasets.sort((a, b) => a.data.reduce((a, b) => a + b, 0) - b.data.reduce((a, b) => a + b, 0))
    },
    getLastSunday (date) {
      // Clone the date object so we don't modify the original
      const d = new Date(date)
      // Get the day of the week (begins on Monday)
      const day = d.getDay()
      // Subtract the day of the week from the date to get the last Sunday
      d.setDate(d.getDate() - day)
      // Return the date as a string in the format 'YYYY-MM-DD'
      return d
    },
    setDates (weeks) {
      const today = new Date()
      // take 3 days back (GSC data is 3 days delayed)
      today.setDate(today.getDate() - 3)
      const endDate = this.getLastSunday(today)
      const startDate = new Date()
      startDate.setDate(endDate.getDate() - (weeks * 7) + 1)
      this.date = [startDate.toISOString().slice(0, 10), endDate.toISOString().slice(0, 10)]
    },
    async deleteArticle (articleId) {
      if (articleId !== this.articleId && !confirm('Are you sure you want to delete the competing cluster? This will also exclude all keywords in it.')) return
      this.isLoading.deleteArticles = true

      try {
        this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'delete',
          articleIds: [articleId]
        })
        this.recalculateAlerts({
          contentplanId: this.contentplanId
        })
      } catch (error) {
        console.log(error)
      }

      if (articleId === this.articleId) {
        this.dialogDelete = false
        this.$router.push({
          name: 'Plan',
          params: {
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId
          }
        })
      } else {
        this.competingClusters = this.competingClusters.filter(x => x.article_id !== articleId)
        this.dialogDeleteCompeting = false
      }

      this.isLoading.deleteArticles = false
    },
    async resetArticle (articleId) {
      if (articleId !== this.articleId && !confirm('Are you sure you want to reset the competing cluster? This will delete the cluster and reset the keywords in it.')) return
      this.isLoading.resetArticles = true

      try {
        this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'reset',
          articleIds: [articleId]
        })
        this.recalculateAlerts({
          contentplanId: this.contentplanId
        })
      } catch (error) {
        console.log(error)
      }

      if (articleId === this.articleId) {
        this.dialogDelete = false
        this.$router.push({
          name: 'Plan',
          params: {
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId
          }
        })
      } else {
        this.competingClusters = this.competingClusters.filter(x => x.article_id !== articleId)
        this.dialogDeleteCompeting = false
      }

      this.isLoading.resetArticles = false
    },
    async mergeCluster (articleId) {
      if (!confirm('Are you sure you want to merge these clusters? This will move all the keywords from the competing cluster into this one.')) return
      this.isLoading.mergeCluster = articleId

      try {
        await this.articlePut({
          contentplanId: this.contentplanId,
          articleIdsFrom: [articleId],
          articleIdTo: this.articleId,
          action: 'merge'
        })
        await this.recalculateAlerts({
          contentplanId: this.contentplanId
        })
      } catch (error) {
        console.log(error)
      }

      await this.loadData()
      this.isLoading.mergeCluster = false
    },
    async changeTopic (topicId) {
      this.isLoading.changeTopic = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateTopic',
          articleIds: [this.articleId],
          topicId
        })
        await this.articleGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey,
          includeExtendedMetrics: true
        })
      } finally {
        this.isLoading.changeTopic = false
      }
    },
    async unexcludeKeywords () {
      this.isLoading.unexcludeKeywords = true
      const keywordsToUnexclude = this.clusterKeywords('excluded').map(keyword => keyword.id)
      console.log('keywordsToUnexclude', keywordsToUnexclude)

      try {
        await this.keywordPut({
          contentplanId: this.contentplanId,
          keywordIds: keywordsToUnexclude,
          action: 'reset'
        })
        await this.keywordGetListByPage({
          contentplanId: this.contentplanId,
          pageId: this.article.page_id,
          sharingKey: this.sharingKey
        })
        this.groupKeywordsByCluster()
      } catch (error) {
        console.log(error)
      }

      this.isLoading.unexcludeKeywords = false
    },
    async handleDialogClick () {
      this.dialogModifyArticle = !this.dialogModifyArticle
      if (this.dialogModifyArticle) this.dialogKey += 1
    },
    async handleCloseDendrogram () {
      this.dialogModifyArticle = false // Close the dialog
      await this.loadData() // Refresh the article data
    },
    async handleWriteArticle () {
      if (this.isArticleEmptyOfContent && this.activeWorkspace && this.activeWorkspace.remaining_article_credits <= 0) {
        this.setSnackbar({ open: true, text: 'You have no article credits remaining. Upgrade your plan to produce more articles.', type: 'error' })
        return
      }

      this.isLoading.writeArticle = true
      try {
        const statusToUse = this.article.status
        const stepsObject = this.getStepsObjectFromStatus(statusToUse)
        // add progress tracker
        this.addProgressTracker({
          contentplanId: this.contentplanId,
          clusterId: this.articleId,
          name: this.title,
          stepsObject
        })

        await this.sendWebsocketMessage({
          action: 'runOrchestrator',
          message: {
            contentplanId: this.contentplanId,
            clusterIds: [this.articleId],
            runWriting: true,
            selectBy: 'cluster',
            publishTo: this.activeContentplan.main_cms_integration || 'autoblog',
            userType: 'managed'
          }
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading.writeArticle = false
      }
    },
    openRewriteDialog () {
      this.rewriteStatus = null
      this.showRewriteDialog = true
    },
    async refreshEditorContent () {
      if (!this.articleId) return

      this.isLoading.refreshEditorContent = true
      try {
        const response = await this.articleGet({
          contentplanId: this.contentplanId,
          articleId: this.articleId,
          sharingKey: this.sharingKey,
          includeExtendedMetrics: false,
          straightReturn: true,
          skipCommit: true
        })
        const latestArticle = response?.items?.[0]
        if (!latestArticle) return

        const latestBrief = latestArticle.description || ''
        const latestResearch = latestArticle.research || ''
        const latestDraft = latestArticle.draft || ''
        const latestFinalDraft = latestArticle.final || ''

        this.autosave.brief.lastSavedContent = latestBrief
        this.autosave.research.lastSavedContent = latestResearch
        this.autosave.draft.lastSavedContent = latestDraft
        this.autosave.finalDraft.lastSavedContent = latestFinalDraft

        this.description = latestBrief
        this.research = latestResearch
        this.draft = latestDraft
        this.finalDraft = latestFinalDraft
        this.notesPreviewObject = {
          brief: latestBrief,
          research: latestResearch,
          draft: latestDraft,
          final: latestFinalDraft
        }
      } catch (error) {
        console.error('Error refreshing editor content:', error)
      } finally {
        this.isLoading.refreshEditorContent = false
      }
    },
    toggleCopilot () {
      this.showCopilot = !this.showCopilot
    },
    closeRewriteDialog () {
      this.showRewriteDialog = false
      this.rewriteStatus = null
    },
    async handleUploadCluster () {
      if (!this.article) return

      if (this.isUploadDisabled) {
        this.setSnackbar({
          open: true,
          text: this.uploadDisabledMessage,
          type: 'error'
        })
        return
      }

      if (!this.activeContentplan.main_cms_integration) {
        this.setSnackbar({
          open: true,
          text: 'CMS integration is not configured. Please set up a CMS integration first.',
          type: 'error'
        })
        return
      }

      let uploadDecision = null
      try {
        const decisionContext = await this.prepareUploadDecisionForArticle(this.articleId)
        uploadDecision = decisionContext.decision
        const isConfirmed = await this.confirmSingleUpload(this.article?.title, uploadDecision)
        if (!isConfirmed) return
      } catch (error) {
        console.error('Error preparing upload decision:', error)
        this.setSnackbar({
          open: true,
          text: 'Could not verify upload target. Please try again.',
          type: 'error'
        })
        return
      }

      this.isLoading.upload = true

      try {
        await this.uploadArticle({
          contentplanId: this.contentplanId,
          clusterId: this.articleId,
          publishTo: this.activeContentplan.main_cms_integration,
          cmsIdOverride: uploadDecision?.cmsIdOverride || undefined
        })

        this.setSnackbar({
          open: true,
          text: 'Article uploaded successfully',
          type: 'success'
        })

        await this.loadData()
      } catch (error) {
        console.error('Error uploading article:', error)
        this.setSnackbar({
          open: true,
          text: error?.response?.data?.message || 'Failed to upload article. Please try again.',
          type: 'error'
        })
      } finally {
        this.isLoading.upload = false
      }
    },
    async confirmRewrite () {
      if (!this.article) return
      this.isLoading.rewrite = true
      const articleId = this.articleId
      const articleTitle = this.article.title

      try {
        if (this.rewriteStatus) {
          await this.articlePut({
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId,
            action: 'updateStatus',
            articleIds: [articleId],
            status: this.rewriteStatus
          })
        }

        const statusToUse = this.rewriteStatus || this.article.status
        const stepsObject = this.getStepsObjectFromStatus(statusToUse)

        this.addProgressTracker({
          contentplanId: this.contentplanId,
          clusterId: articleId,
          name: articleTitle,
          stepsObject
        })

        await this.sendWebsocketMessage({
          action: 'runOrchestrator',
          message: {
            contentplanId: this.contentplanId,
            clusterIds: [articleId],
            runWriting: true,
            selectBy: 'cluster',
            publishTo: this.activeContentplan.main_cms_integration || 'autoblog',
            userType: 'managed'
          }
        })

        this.closeRewriteDialog()
      } catch (error) {
        console.error('Error in rewrite:', error)
      } finally {
        this.isLoading.rewrite = false
      }
    }
  },
  components: { ChartComponent, Dendrogram, ChatGPT, MarkdownEditor }
}
